import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { DateTime } from "luxon";
import { CustomDate, getDateDimensionNames } from "modules/customer/insights/cost/customDate";

export class MonthlyCosts {
    public readonly storeId: number;
    public readonly date: DateTime;
    public readonly customDate: CustomDate;
    public readonly costType: string;
    public readonly costValue: number;
    public readonly group: string;

    constructor(
        storeId: number,
        date: DateTime,
        customDate: CustomDate,
        costType: string,
        costValue: number,
        group: string
    ) {
        this.storeId = storeId;
        this.date = date;
        this.customDate = customDate;
        this.costType = costType;
        this.costValue = costValue;
        this.group = group;
    }
}

export const loadMonthlyCosts = (
    costReferenceDate: DateTime,
    useCustomCalendar: boolean,
    storeId?: number,
    similarStoresIds?: number[],
    costType?: string
): AppThunk<Promise<MonthlyCosts[]>> => async (dispatch) => {
    try {
        if (!costReferenceDate || !storeId || !similarStoresIds || !costType) {
            return [];
        }

        const priorFiveYearsToReferenceDate = costReferenceDate.minus({ years: 5 }).plus({ days: 1 }).startOf("day");
        const dateDimensions = getDateDimensionNames(useCustomCalendar);

        const storeAndSimilarStoreIds = [storeId].concat(...similarStoresIds).map(String);

        const costQuery = {
            measures: ["F_Cost.SumCostValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorFiveYearsToReferenceDate, costReferenceDate.endOf('day')]
            }],
            filters: [
                {
                    member: "D_Account.AccountName",
                    operator: "equals",
                    values: [String(costType)]
                },
                {
                    member: "D_Store.PK_Store",
                    operator: "equals",
                    values: storeAndSimilarStoreIds
                }
            ], // Update this and monthly revenue to use custom date dimensions
            dimensions: [
                "D_Store.PK_Store",
                `D_Date.${dateDimensions.monthPeriodStartDate}`,
                `D_Date.${dateDimensions.monthPeriodNumber}`,
                "D_Date.QuarterNo",
                `D_Date.${dateDimensions.yearNumber}`,
                "D_Account.AccountName",
                "D_Store.Group"
            ],
            order: [[`D_Date.${dateDimensions.monthPeriodStartDate}`, "asc"]],
        };

        const costPromise = dispatch(cubeLoad(costQuery));

        const result = await Promise.all([costPromise]) as unknown as ResultSet[];

        const costRawData = result[0].rawData();

        return costRawData.map(costRow => {
            return new MonthlyCosts(
                Number(costRow["D_Store.PK_Store"]),
                DateTime.fromISO(costRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                new CustomDate(
                    DateTime.fromISO(costRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                    Number(costRow[`D_Date.${dateDimensions.monthPeriodNumber}`]),
                    Number(costRow["D_Date.QuarterNo"]),
                    Number(costRow[`D_Date.${dateDimensions.yearNumber}`]),
                ),
                costRow["D_Account.AccountName"],
                Number(costRow["F_Cost.SumCostValue"]),
                costRow["D_Store.Group"]
            );
        });
    } catch (error) {
        dispatch(logError("Error loading MonthlyCosts.", error));
        throw error;
    }
};
